<script lang="ts">
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { Button, Input, Text, Token } from '@Shelly/shelly-design-system/index';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { real_id } from 'src/helpers/interface_helpers';
	import { buttonOptions } from 'src/helpers/settings/settings_config_helpers';
	import { valueOutOfRange } from 'src/helpers/utilities';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
		import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { format } from 'svelte-i18n';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	$: settingsStore = getDeviceSettings(id);
	export let device;

	let id = real_id(device.id),
		settings = $settingsStore,
		autoDim = settings?.ui?.brightness?.auto_dim?.enable,
		cacheAutoDim = autoDim,
		loading = false,
		timeout = settings?.ui?.brightness?.auto_dim?.timeout || 0,
		cacheTimeout = timeout,
		selected =
			buttonOptions()?.find((opt) => opt.value === timeout)?.key ??
			(timeout === 0 ? 'Off' : undefined),
		customTimeout = !selected && timeout > 0 ? timeout : '';

	const sendRequest = async () => {
		const selectedOption = buttonOptions()?.find((opt) => opt.key === selected);
		const value = selectedOption ? selectedOption.value : Number(customTimeout);

		if (cacheTimeout === value && cacheAutoDim === autoDim) return;

		loading = true;

		autoDim = value > 0;

		const params = {
			id: device.channel,
			config: {
				brightness: { auto_dim: { enable: autoDim, timeout: value } },
			},
		};

		const res: SettingRequest = await gen2Request(device, `ui.SetConfig`, params);
		const response = await onSettingsResponse(res, device);
		if (response) {
			cacheTimeout = value;
			cacheAutoDim = autoDim;
			settings.ui = {
				...settings.ui,
				...params.config,
			};
			devices_settings.update_devices_settings(id, settings);
		}
		loading = false;
	};

	const tokenClickHandler = (key: string) => {
		selected = key;
		customTimeout = '';
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({ onClick: sendRequest, loading, disabled: loading });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

<div class="d-flex justify-content-between">
	{#each buttonOptions() as option}
		<Token
			id="token-0d9p81s"
			size="m"
			title={option.title}
			on:click={() => tokenClickHandler(option.key)}
			selected={selected === option.key}
		/>
	{/each}
</div>
<Text styleType="body-small" class="mt-s mb-s">
	{$format('text_screen_off_timeout_description', {
		default: 'Choose from the predefined timeouts or set a custom timeout in seconds.',
	})}
</Text>
<Input
	aria-label={$format('input_screen_off_timeout', {
		default: 'Screen off timeout',
	})}
	type="number"
	icon="fas fa-clock"
	value={customTimeout}
	error={valueOutOfRange(customTimeout, 0, 60)
		? $format('min_max_error_description', {
				values: { min: 0, max: 60 },
				default: 'Value must be between {min} and {max}.',
			})
		: ''}
	on:input={(e) => {
		customTimeout = e.target['value'];
		selected = undefined;
	}}
	id="input-x7wmadsv56f0"
/>

{#if !$showNewLayout}
	<Button class="mt-s" {loading} size="m" width="full" on:click={sendRequest}>
		{$format('button_save')}
	</Button>
{/if}
